/**
 * What the product page may tell someone without Premium about store stock
 * (#392): how many states, never which ones.
 *
 * `overview` is the answer /api/v1/stocks/sku/{sku}/states gives everyone. It
 * counts stock_state rows, which only the Best Buy poller writes, so on its own
 * it would call a product unavailable while Micro Center has it on a shelf (SKU
 * 6683657 on 2026-10-07: 0 states, 12 Micro Center stores). The flags are the
 * ones behind the availability pills on the page.
 *
 * Returns null when there is nothing safe to say, so the page stays quiet
 * rather than guessing. The Best Buy count is not used when:
 *  - it has not loaded, or the product has never been scanned (no rows);
 *  - Best Buy is also shipping the product. The poller only rescans states for
 *    products that are not online, so the rows are frozen at whatever the last
 *    scan saw (SKU 6589134 on 2026-10-07: "40 of 50" from 2026-09-27);
 *  - the scan found no state while Best Buy's own flag says in store (#383).
 * Micro Center's flag is not used when the API marks its data stale.
 */
export function storeAvailabilitySentence({
    overview,
    bestBuyInStore,
    bestBuyOnline,
    microCenterInStore,
    microCenterStale,
}) {
    const countIsCurrent = Number(overview?.totalStatesCount) > 0 && !bestBuyOnline;
    const states = countIsCurrent ? Number(overview.totalStatesAvailable) || 0 : 0;
    const atMicroCenter = !!microCenterInStore && !microCenterStale;

    if (states > 0) {
        const bestBuy = `In stock for pickup at Best Buy in ${states} ${states === 1 ? 'state' : 'states'}`;
        return atMicroCenter ? `${bestBuy} and at Micro Center right now.` : `${bestBuy} right now.`;
    }

    if (atMicroCenter) {
        return 'In stock at Micro Center stores right now.';
    }

    // Says pickup, not "any store": the product may still be orderable online elsewhere.
    return countIsCurrent && !bestBuyInStore && !microCenterStale
        ? 'Not available for store pickup right now.'
        : null;
}
